/**
 * SUR PRIMICIA CONTROL — API client + auth context.
 * Consumes /api/control/* (Etapa 7). Never touches DB directly.
 */
import axios from "axios";
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/control`;
const ADMIN_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin`;
const STORAGE_TOKEN = "spc.access_token";
const STORAGE_USER = "spc.user";
const STORAGE_SID = "spc.session_id";

export const client = axios.create({ baseURL: API_BASE, timeout: 20000 });

// Editor uses admin endpoints with the same JWT — see ControlEditorPage.
// Long timeout for media uploads (mobile networks).
export const adminClient = axios.create({ baseURL: ADMIN_BASE, timeout: 60000 });

const _attachAuth = (config) => {
  const t = localStorage.getItem(STORAGE_TOKEN);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
};
client.interceptors.request.use(_attachAuth);
adminClient.interceptors.request.use(_attachAuth);

// 401 → session expired: clear local storage and hard-redirect to /control/login
// so the user sees the login screen instead of a silently-empty UI.
const _on401 = (err) => {
  const status = err && err.response && err.response.status;
  if (status === 401 && typeof window !== "undefined") {
    try {
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_USER);
      localStorage.removeItem(STORAGE_SID);
    } catch (_e) { /* ignore */ }
    if (!window.location.pathname.endsWith("/control/login")) {
      window.location.replace("/control/login");
    }
  }
  return Promise.reject(err);
};
client.interceptors.response.use((r) => r, _on401);
adminClient.interceptors.response.use((r) => r, _on401);

export const controlApi = {
  login: (email, password, deviceName) =>
    client.post("/login", { email, password, device_name: deviceName || navigatorLabel() })
      .then((r) => r.data),
  logout: () => client.post("/logout").then((r) => r.data),
  logoutAll: () => client.post("/logout-all").then((r) => r.data),
  sessions: () => client.get("/sessions").then((r) => r.data),
  revokeSession: (sid) => client.post(`/sessions/${sid}/revoke`).then((r) => r.data),

  dashboard: () => client.get("/dashboard").then((r) => r.data),
  nextRun: () => client.get("/next-run").then((r) => r.data),

  articles: (params = {}) => client.get("/articles", { params }).then((r) => r.data),
  article: (id) => client.get(`/articles/${id}`).then((r) => r.data),

  // Publicar Ahora (Manual Override)
  candidates: (params = {}) => client.get("/candidates", { params }).then((r) => r.data),
  publishCandidateNow: (id, opts = {}, idempotencyKey) =>
    client.post(`/candidates/${id}/publish`, opts || {}, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
      timeout: 60000,
    }).then((r) => r.data),
  publishSocial: (articleId, platforms) =>
    client.post(`/articles/${articleId}/publish-social`, { platforms }, { timeout: 60000 })
      .then((r) => r.data),
  removeArticle: (id, reason, expectedVersion, idempotencyKey) =>
    client.delete(`/articles/${id}`, {
      data: { reason: reason || "" },
      headers: {
        ...(expectedVersion ? { "If-Match": expectedVersion } : {}),
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
    }).then((r) => r.data),
  patchDistribution: (id, patch, idempotencyKey) =>
    client.patch(`/articles/${id}/distribution`, patch, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
    }).then((r) => r.data),
  retryFacebook: (id, key) =>
    client.post(`/articles/${id}/retry/facebook`, null, { headers: key ? { "Idempotency-Key": key } : {} })
      .then((r) => r.data),
  retryInstagram: (id, key) =>
    client.post(`/articles/${id}/retry/instagram`, null, { headers: key ? { "Idempotency-Key": key } : {} })
      .then((r) => r.data),

  automation: () => client.get("/automation").then((r) => r.data),
  patchAutomation: (patch) => client.patch("/automation", patch).then((r) => r.data),

  runs: (params = {}) => client.get("/runs", { params }).then((r) => r.data),
  run: (id) => client.get(`/runs/${id}`).then((r) => r.data),
  events: (params = {}) => client.get("/events", { params }).then((r) => r.data),
  event: (id) => client.get(`/events/${id}`).then((r) => r.data),
  sources: () => client.get("/sources").then((r) => r.data),
  patchSource: (id, patch) => client.patch(`/sources/${id}`, patch).then((r) => r.data),

  incidents: (params = {}) => client.get("/incidents", { params }).then((r) => r.data),
  incident: (id) => client.get(`/incidents/${id}`).then((r) => r.data),
  ackIncident: (id) => client.post(`/incidents/${id}/acknowledge`).then((r) => r.data),

  activity: (limit = 30) => client.get("/activity", { params: { limit } }).then((r) => r.data),
  statsToday: () => client.get("/stats/today").then((r) => r.data),

  config: () => client.get("/config").then((r) => r.data),
  patchConfig: (patch) => client.patch("/config", patch).then((r) => r.data),

  devices: () => client.get("/devices").then((r) => r.data),
  patchCurrentDevice: (patch) => client.patch("/devices/current", patch).then((r) => r.data),

  notifications: (unreadOnly = false, limit = 30) =>
    client.get("/notifications", { params: { unread_only: unreadOnly, limit } }).then((r) => r.data),
  notificationCount: () => client.get("/notifications/count").then((r) => r.data),
  markRead: (id) => client.post(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => client.post("/notifications/mark-all-read").then((r) => r.data),

  // Etapa 9 — Editorial Learning
  learning: (days = 30) => client.get("/editorial-learning", { params: { days } }).then((r) => r.data),
  suggestions: (status) => client.get("/editorial-suggestions", { params: status ? { status } : {} }).then((r) => r.data),
  suggestion: (id) => client.get(`/editorial-suggestions/${id}`).then((r) => r.data),
  acceptSuggestion: (id) => client.post(`/editorial-suggestions/${id}/accept`).then((r) => r.data),
  rejectSuggestion: (id) => client.post(`/editorial-suggestions/${id}/reject`).then((r) => r.data),
  runLearningNow: () => client.post("/editorial-learning/run-now").then((r) => r.data),
  articleFeedback: (id, kind, reasonCategory) =>
    client.post(`/articles/${id}/feedback`, { kind, reason_category: reasonCategory || null }).then((r) => r.data),

  // ============ EDITOR (Mobile-First) — reuses admin endpoints ============
  // Categories & authors are read-only picklists.
  listCategories: () => adminClient.get("/categories").then((r) => r.data),
  listAuthors: () => adminClient.get("/authors").then((r) => r.data),
  // Full article (edit mode)
  getArticle: (id) => adminClient.get(`/articles/${id}`).then((r) => r.data),
  createArticle: (payload) => adminClient.post("/articles", payload).then((r) => r.data),
  updateArticle: (id, payload) => adminClient.put(`/articles/${id}`, payload).then((r) => r.data),
  // Candidate (imported_article) editing
  getCandidate: (id) => adminClient.get(`/automation/imported/${id}`).then((r) => r.data),
  patchCandidate: (id, patch) =>
    adminClient.patch(`/automation/imported/${id}`, patch).then((r) => r.data),
  // Media upload — reuses admin MediaService (S3, NO base64)
  uploadMedia: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return adminClient
      .post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      })
      .then((r) => r.data);
  },
  // AI Assist — reuses /admin/ai/assist (Claude Sonnet 4.5)
  aiAssist: (task, payload) =>
    adminClient
      .post("/ai/assist", { task, ...payload }, { timeout: 90000 })
      .then((r) => r.data),
};

function navigatorLabel() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Navegador";
}

/* ============================================================
 * Auth Context
 * ============================================================ */
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_USER) || "null"); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_TOKEN));
  const [sid, setSid] = useState(() => localStorage.getItem(STORAGE_SID));

  const login = useCallback(async (email, password, deviceName) => {
    const r = await controlApi.login(email, password, deviceName);
    localStorage.setItem(STORAGE_TOKEN, r.access_token);
    localStorage.setItem(STORAGE_USER, JSON.stringify(r.user));
    localStorage.setItem(STORAGE_SID, r.session_id);
    setToken(r.access_token); setUser(r.user); setSid(r.session_id);
    return r;
  }, []);

  const logout = useCallback(async () => {
    try { await controlApi.logout(); } catch (_e) { /* ignore */ }
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_SID);
    setToken(null); setUser(null); setSid(null);
  }, []);

  // Global 401 handler
  useEffect(() => {
    const id = client.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err && err.response && err.response.status === 401) {
          localStorage.removeItem(STORAGE_TOKEN);
          localStorage.removeItem(STORAGE_USER);
          localStorage.removeItem(STORAGE_SID);
          setToken(null); setUser(null); setSid(null);
        }
        return Promise.reject(err);
      }
    );
    return () => client.interceptors.response.eject(id);
  }, []);

  const value = useMemo(() => ({ user, token, sid, login, logout }), [user, token, sid, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ============================================================
 * Realtime SSE (with polling fallback). Auto-reconnect.
 * ============================================================ */
export function useControlStream(onEvent) {
  const [connected, setConnected] = useState(false);
  const cbRef = useRef(onEvent);
  useEffect(() => { cbRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_TOKEN);
    if (!token) return () => {};
    let es;
    let retryTimer;
    let attempts = 0;
    const connect = () => {
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/control/stream?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);
      es.addEventListener("open", () => { setConnected(true); attempts = 0; });
      es.addEventListener("error", () => {
        setConnected(false);
        try { es.close(); } catch { /* noop */ }
        const delay = Math.min(30000, 1000 * Math.pow(2, attempts++));
        retryTimer = setTimeout(connect, delay);
      });
      const generic = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (cbRef.current) cbRef.current(ev.type, data);
        } catch { /* noop */ }
      };
      // Listen to all named events
      ["hello", "ping", "article.removed", "article.distribution_changed",
        "automation.changed", "source.updated",
        "facebook.retry_queued", "instagram.retry_queued",
        "incident.acknowledged", "config.changed"].forEach((name) => {
        es.addEventListener(name, generic);
      });
    };
    connect();
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      try { es && es.close(); } catch { /* noop */ }
      setConnected(false);
    };
  }, []);

  return connected;
}

/* ============================================================
 * Online / Offline hook
 * ============================================================ */
export function useOnlineStatus() {
  const [online, setOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
